import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { getRequest } from '../../apis/ApiInstence';
import { Calendar, User, ArrowLeft, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import headerBg from '../../Assets/img/parshvanath-temple-khajuraho.jpg';

const BlogDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const assetBaseUrl = useMemo(() => {
    let base = process.env.REACT_APP_BACKEND || '';
    base = base.replace('/khajuraho/', '/');
    if (!base.endsWith('/')) {
      base = `${base}/`;
    }
    return base;
  }, []);

  const formatDate = (value) => {
    if (!value) return 'Recent';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Recent';
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  const formatContent = (content) => {
    if (!content) return '';
    let html = content;
    // Replace <p><b>...</b></p> with <h2>...</h2>
    html = html.replace(/<p>\s*<(strong|b)>(.*?)<\/\1>\s*<\/p>/gi, '<h2>$2</h2>');
    // Replace <div><b>...</b></div> with <h2>...</h2>
    html = html.replace(/<div>\s*<(strong|b)>(.*?)<\/\1>\s*<\/div>/gi, '<h2>$2</h2>');
    // Replace standalone <b>...</b> with <h2>...</h2>
    html = html.replace(/(?:<br\s*\/?>|\n|^)\s*<(strong|b)>(.*?)<\/\1>\s*(?:<br\s*\/?>|\n|$)/gi, '<h2>$2</h2>');
    
    // Auto-detect plain text headings (lines that are short and don't end with a period)
    const lines = html.split('\n');
    const processedLines = lines.map(line => {
      const trimmed = line.trim();
      // If the line is standalone, short, doesn't end in a period, and doesn't look like an HTML tag
      if (trimmed.length > 0 && trimmed.length < 100 && !trimmed.endsWith('.') && !trimmed.startsWith('<')) {
        return `<h2>${trimmed}</h2>`;
      }
      return line;
    });
    
    return processedLines.join('\n');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchBlog = async () => {
      setLoading(true);
      try {
        const response = await getRequest(`blogs/${id}`);
        const blogData = response?.data?.data || response?.data;
        if (blogData) {
          let parsedImages = [];
          if (Array.isArray(blogData.images)) {
            parsedImages = blogData.images;
          } else if (typeof blogData.images === 'string' && blogData.images.trim().length > 0) {
            try {
              const maybeArray = JSON.parse(blogData.images);
              parsedImages = Array.isArray(maybeArray) ? maybeArray : [blogData.images];
            } catch (e) {
              parsedImages = [blogData.images];
            }
          }
          const firstImage = parsedImages.length > 0 ? parsedImages[0] : '';
          const image = firstImage
            ? (firstImage.startsWith('http') ? firstImage : `${assetBaseUrl}${String(firstImage).replace(/^\/+/, '')}`)
            : 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2000&auto=format&fit=crop';
          
          setBlog({
            ...blogData,
            image,
            title: blogData.heading || 'Untitled',
            date: formatDate(blogData.createdAt),
            author: blogData.author || 'Khajuraho Routes Team',
          });
        }
      } catch (error) {
        console.error("Error fetching blog details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id, assetBaseUrl, i18n.language]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 font-medium">{t('blogdetail_loading')}</div>;
  }

  if (!blog) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 font-medium">{t('blogdetail_not_found')}</div>;
  }

  return (
    <main className="w-full bg-[#f4f5f7] text-gray-900 overflow-hidden font-sans pb-24">
      <Helmet>
        <title>{`${blog.title} | Khajuraho Routes`}</title>
        <meta name="description" content={`Read "${blog.title}" on Khajuraho Routes. ${blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 120) : ''}...`} />
        <meta name="keywords" content={`${blog.title}, Khajuraho travel blog, Khajuraho news`} />
        <link rel="canonical" href={`https://khajurahoroutes.com/news-media/${id}`} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://khajurahoroutes.com/news-media/${id}`} />
        <meta property="og:title" content={`${blog.title} | Khajuraho Routes`} />
        <meta property="og:description" content={`Read "${blog.title}" on Khajuraho Routes. ${blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 120) : ''}...`} />
        <meta property="og:image" content={blog.image} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${blog.title} | Khajuraho Routes`} />
        <meta name="twitter:description" content={`Read "${blog.title}" on Khajuraho Routes. ${blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 120) : ''}...`} />
        <meta name="twitter:image" content={blog.image} />

        {/* JSON-LD Blog Schema */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "headline": "${blog.title.replace(/"/g, '\\"')}",
              "image": "${blog.image}",
              "author": {
                "@type": "Person",
                "name": "${blog.author || 'Khajuraho Routes'}"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Khajuraho Routes",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://khajurahoroutes.com/android-chrome-512x512.png"
                }
              },
              "datePublished": "${blog.date}",
              "description": "${blog.content ? blog.content.replace(/<[^>]*>?/gm, '').replace(/"/g, '\\"').substring(0, 200) : ''}"
            }
          `}
        </script>
      </Helmet>
      {/* Redesigned Premium Hero Section */}
      <section className="relative w-full pt-32 pb-40 sm:pt-40 sm:pb-52 lg:pt-52 lg:pb-64 overflow-hidden bg-gray-900">
        <div className="absolute inset-0">
          {/* Hero Image Background */}
          <img 
            src={headerBg} 
            className="w-full h-full object-cover opacity-70" 
            alt="Khajuraho travel blog - temples and Central India stories" 
            decoding="async"
          />
          <div className="absolute inset-0 bg-gray-900/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#f4f5f7] via-transparent to-black/50" />
        </div>
        
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/news-media" className="inline-flex items-center gap-2 text-gray-200 hover:text-white mb-10 transition-all font-semibold uppercase tracking-widest text-[10px] sm:text-xs bg-white/10 px-6 py-2.5 rounded-full backdrop-blur-md border border-white/20 hover:bg-white/20 hover:scale-105 shadow-lg">
              <ArrowLeft size={14} /> {t('blogdetail_back')}
            </Link>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[1.1] tracking-tighter mb-10 drop-shadow-2xl max-w-5xl"
          >
            {blog.title}
          </motion.h1>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-gray-200"
          >
            <div className="flex items-center gap-2.5 bg-black/30 px-6 py-3 rounded-2xl border border-white/10 backdrop-blur-md shadow-2xl">
              <Calendar size={18} className="text-orange-main drop-shadow-md" />
              <span className="font-bold tracking-widest text-xs uppercase">{blog.date}</span>
            </div>
            <div className="flex items-center gap-2.5 bg-black/30 px-6 py-3 rounded-2xl border border-white/10 backdrop-blur-md shadow-2xl">
              <User size={18} className="text-orange-main drop-shadow-md" />
              <span className="font-bold tracking-widest text-xs uppercase">{blog.author}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Redesigned Premium Content Section */}
      <section className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 -mt-32 sm:-mt-40 md:-mt-48 relative z-20">
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="bg-white rounded-[2.5rem] sm:rounded-[3.5rem] p-6 sm:p-12 md:p-16 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] border border-gray-100/50 backdrop-blur-3xl"
        >
          
          {/* Un-cropped, beautifully presented main image */}
          {blog.image && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mb-14 w-full rounded-[2.5rem] overflow-hidden bg-gray-50 flex justify-center items-center shadow-inner border border-gray-100/80 p-2 sm:p-4"
            >
               <img 
                 src={blog.image} 
                 alt={blog.title} 
                 loading="lazy"
                 decoding="async"
                 className="w-full h-auto max-h-[500px] sm:max-h-[700px] object-contain rounded-[2rem] shadow-sm"
              />
            </motion.div>
          )}

          <div 
            className="prose prose-lg sm:prose-xl max-w-none text-gray-600 text-justify leading-loose prose-headings:font-extrabold prose-headings:text-gray-900 prose-headings:tracking-tight prose-a:text-orange-main hover:prose-a:text-orange-600 prose-img:rounded-[2rem] prose-img:shadow-2xl prose-img:border prose-img:border-gray-100 prose-p:mb-8"
            style={{ whiteSpace: 'pre-line' }}
            dangerouslySetInnerHTML={{ __html: formatContent(blog.content) }}
          />
        </motion.div>
      </section>
    </main>
  );
};

export default BlogDetails;
